import * as Dialog from '@radix-ui/react-dialog'
import classNames from 'classnames'
import React from 'react'
import { ThemeProvider, useTheme } from '@opallabs/hooks'
import { Button } from '../Button'
import { DrawerSize, DrawerSizeType } from '../Drawer/types'
import { Icon } from '../Icon'
import { ModalDialogContextProvider } from '../ModalDialogContext'
import * as styles from './styles'

interface ContentProps {
  sidebarSize: DrawerSize
  /**
   * Any CSS length, for a sidebar whose width is not one of the `DrawerSize`
   * steps — a width the user can drag, for instance. Overrides `sidebarSize`.
   */
  inlineSize?: DrawerSizeType
  topOffset?: number
  inert?: boolean
}

export { selectors as sidebarSelectors } from './styles'

const Portal: React.FC<React.ComponentProps<typeof Dialog.Portal>> = ({
  children,
  forceMount,
  ...props
}) => {
  const theme = useTheme()

  /**
   * Dialog.Portal mounts its children outside of the DOM hierarchy, typically
   * directly under the <body> element. Because this will move the components
   * outside of the scope of any CSS variables applied in the existing DOM tree,
   * we need to reprovide Gem's <ThemeProvider> with the current theme to rescope them.
   */
  return (
    <Dialog.Portal {...props} forceMount={forceMount}>
      <ThemeProvider
        theme={theme}
        // Portalled components should have absolute/fixed position
        // Reset the size properties, so that the portal itself
        // does not affect the container's layout
        css={{ blockSize: 'auto', inlineSize: 'auto' }}
      >
        {children}
      </ThemeProvider>
    </Dialog.Portal>
  )
}
Portal.displayName = 'Lapidary: Portal ← Sidebar'

const Header: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => (
  <div
    {...props}
    className={classNames([styles.selectors.header, className])}
    css={styles.sidebarHeader}
  />
)
Header.displayName = 'Lapidary: Header ← Sidebar'

const Body: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  children,
  className,
  ...props
}) => (
  <div
    {...props}
    className={classNames([styles.selectors.body, className])}
    css={styles.sidebarBody}
  >
    {children}
  </div>
)
Body.displayName = 'Lapidary: Body ← Sidebar'

const Footer: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div
      className={classNames([styles.selectors.title, className])}
      css={styles.sidebarFooter}
      {...props}
    >
      {children}
    </div>
  )
}
Footer.displayName = 'Lapidary: Footer ← Sidebar'

const Title = React.forwardRef<
  React.ElementRef<typeof Dialog.Title>,
  React.ComponentPropsWithoutRef<typeof Dialog.Title>
>(({ className, ...props }, forwardedRef) => (
  <Dialog.Title
    {...props}
    className={classNames([styles.selectors.title, className])}
    css={styles.title}
    ref={forwardedRef}
  />
))
Title.displayName = 'Lapidary: Title ← Sidebar'

const Description = React.forwardRef<
  React.ElementRef<typeof Dialog.Title>,
  React.ComponentPropsWithoutRef<typeof Dialog.Description>
>(({ className, ...props }, forwardedRef) => {
  return (
    <Dialog.Description
      {...props}
      className={classNames([styles.selectors.description, className])}
      css={styles.description}
      ref={forwardedRef}
    />
  )
})
Description.displayName = 'Lapidary: Description ← Sidebar'

const Close = React.forwardRef<
  React.ElementRef<typeof Dialog.Close>,
  React.ComponentPropsWithoutRef<typeof Dialog.Close>
>(({ className, ...props }, forwardedRef) => {
  return (
    <Dialog.Close
      asChild
      {...props}
      className={classNames([styles.selectors.close, className])}
      ref={forwardedRef}
    >
      <Button
        ariaLabel="Close"
        variant="ghost"
        css={styles.sidebarClose}
        icon={<Icon glyph="gem-close" ariaHidden />}
        name="Close"
      />
    </Dialog.Close>
  )
})
Close.displayName = 'Lapidary: Close ← Sidebar'

const Content = React.forwardRef<
  React.ElementRef<typeof Dialog.Content>,
  React.ComponentProps<typeof Dialog.Content> & ContentProps
>(
  (
    {
      children,
      className,
      sidebarSize,
      inlineSize,
      topOffset,
      inert,
      ...props
    },
    forwardedRef
  ) => {
    return (
      // forceMount on both Portal and Content keeps them in the DOM while the
      // sidebar slides closed. The `visibility` transition hides it at the end
      // of the slide, so a closed sidebar takes no pointer or focus.
      <Portal forceMount>
        <ModalDialogContextProvider>
          <Dialog.Content
            {...props}
            className={classNames([styles.selectors.content, className])}
            forceMount
            onInteractOutside={(e) => e.preventDefault()}
            css={styles.sidebarContentWrapper(
              sidebarSize,
              topOffset,
              inlineSize
            )}
            ref={forwardedRef}
            inert={inert ? '' : undefined}
          >
            {children}
          </Dialog.Content>
        </ModalDialogContextProvider>
      </Portal>
    )
  }
)
Content.displayName = 'Lapidary: Content ← Sidebar'

export const Sidebar = {
  Body,
  Close,
  Content,
  Description,
  Footer,
  Header,
  Root: Dialog.Root,
  Title,
  Trigger: Dialog.Trigger,
}
